import axios from 'axios';
import router from '@/router'
import { API_BASE_URL } from '@/config/runtime';

let store = null;
let isHandlingUnauthorized = false;

export const setStore = (s) => {
  store = s;
};

const session = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  // No cookies/credentials cross-origin: this app authenticates every request
  // with a DRF token in the Authorization header (see setAuthToken), not via
  // session cookies, so there is no CSRF cookie->header mapping here.
  withCredentials: false,
});

/**
 * Single source of truth for the DRF auth token on outbound requests.
 *
 * The token is attached ONLY to the `session` instance — never to the global
 * `axios.defaults`. In axios 0.18 every instance request re-merges the global
 * `axios.defaults` (see node_modules/axios/lib/core/Axios.js:
 * `utils.merge(defaults, this.defaults, config)`), so a token left on the
 * global header leaks onto every request — including /auth/login/, where DRF's
 * TokenAuthentication rejects a stale token with 403 "Nesprávny token." after
 * rest_auth's LogoutView deletes the token row.
 */
export function setAuthToken(token) {
  if (token) {
    session.defaults.headers.Authorization = `Token ${token}`;
  } else {
    delete session.defaults.headers.Authorization;
  }
}

export function clearAuthToken() {
  delete session.defaults.headers.Authorization;
}

session.interceptors.response.use(function (response) {
  return response
}, function (error) {
  const status = error?.response?.status;
  const requestUrl = error?.config?.url;
  const isLoginRequest = requestUrl?.includes('/auth/login/');
  const isLogoutRequest = requestUrl?.includes('/auth/logout/');
  const isAdminNewsRequest = requestUrl?.includes('/admin_news/');
  const isSilentRequest = isLoginRequest || isLogoutRequest || isAdminNewsRequest || error?.config?.suppressErrorAlert;

  if (!error.response) {
    if (process.env.NODE_ENV !== 'production') {
      const errorType = error?.code === 'ECONNABORTED' ? 'timeout' : (error?.code || 'network');
      console.warn('[session] request failed without response', {
        type: errorType,
        method: error?.config?.method,
        url: requestUrl,
      });
    }
    return Promise.reject(error);
  }

  if (status === 500 || status === 404) {
    return Promise.reject(error);
  }

  if (status === 403) {
    if (store && !isSilentRequest)
      store.commit('notifications/raiseError', error.response.data);
    return Promise.reject(error);
  }

  if (store && !isSilentRequest && status !== 401)
    store.commit('notifications/raiseError', error.response.data);

  if (status === 401) {
    const token = store?.state?.auth?.token;
    const isAuthenticated = !!token;
    const isAuthenticating = !!store?.state?.auth?.authenticating;

    if (store && isAuthenticated && !isLoginRequest && !isLogoutRequest && !isHandlingUnauthorized && !isAuthenticating) {
      isHandlingUnauthorized = true;
      try {
        store.commit('auth/LOGOUT');
        store.commit('auth/REMOVE_TOKEN');
        if (router.currentRoute.path !== '/login') {
          router.push('/login');
        }
      } finally {
        isHandlingUnauthorized = false;
      }
    }
  }

  return Promise.reject(error);
})


export default session;

